// Copyright 2018 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { Point3D } from "datatypes/SoramaWebApi";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { IHardwareService } from "../Hardware/IHardwareService";

export class MotionDevice implements IHardwareDevice {
    id: string;
    name: string;
    range: Point3D;
    isHomed?: boolean;
    baseApi: string;
}

@autoinject
export class MotionDeviceDiscoveryService implements IHardwareService<MotionDevice> {
    private readonly userSettingsService: IUserSettingsService;
    private readonly webrequestHandler: IWebRequestHandler;
    private readonly localApi: ILocalApi;

    private static readonly moduleSettingsGroupKey: string = "MotionDeviceDiscoveryService";

    private soramaMotionUrl: string;
    private motionExternalUrls: string[] = [];
    public get baseUrls(): string[] {
        return this.motionExternalUrls.concat(this.soramaMotionUrl);
    }

    public hardware: MotionDevice[];
    public isRefreshing: boolean;

    constructor(webrequestHandler: IWebRequestHandler, userSettingsService: IUserSettingsService, localApi: ILocalApi) {
        this.webrequestHandler = webrequestHandler;
        this.userSettingsService = userSettingsService;
        this.localApi = localApi;

        this.userSettingsService.bind(
            MotionDeviceDiscoveryService.moduleSettingsGroupKey,
            "motionExternalUrls",
            this,
            (x) => JSON.stringify(x),
            (x) => JSON.parse(x)
        );

        this.refresh();
    }

    public refresh(): Promise<void> {
        this.isRefreshing = true;

        const promises: Promise<MotionDevice[]>[] = [];

        return this.localApi.motion
            .then((x) => (this.soramaMotionUrl = x))
            .then(() => {
                for (const url of this.baseUrls) {
                    const promise = this.webrequestHandler
                        .createGetRequest<MotionDevice[]>(url)
                        .send()
                        .then((x) => {
                            for (const device of x.content) {
                                device.baseApi = url;
                            }
                            return x.content;
                        })
                        // Absorb error with external APIs
                        .catch((e) => {
                            console.log("Unable to get response from: " + url + ": " + e.message);
                            return undefined;
                        });

                    promises.push(promise);
                }

                return Promise.all(promises).then((x) => {
                    const hardware: MotionDevice[] = [];
                    for (const devices of x) {
                        if (devices) {
                            hardware.push(...devices);
                        }
                    }

                    // Abs is required for backwards compatibility
                    this.hardware = hardware.map((y) => {
                        y.range.z = Math.abs(y.range.z);
                        return y;
                    });
                    this.isRefreshing = false;
                });
            });
    }

    public getHardwareById(id: string): Promise<MotionDevice> {
        const findRequest = () => {
            if (!this.hardware) return undefined;

            const idx = this.hardware.findIndex((x) => x.id === id);

            if (idx !== -1) return this.hardware[idx];
            else return undefined;
        };

        const device = findRequest();

        if (device) return Promise.resolve(device);

        return this.refresh().then(() => {
            return Promise.resolve(findRequest());
        });
    }

    public addApiEndpoint(url: string): void {
        if (url !== "" && this.motionExternalUrls.findIndex((x) => x == url) === -1) {
            this.motionExternalUrls.push(url);
            this.userSettingsService.set(
                MotionDeviceDiscoveryService.moduleSettingsGroupKey,
                "motionExternalUrls",
                JSON.stringify(this.motionExternalUrls)
            );
        }
    }

    public removeApiEndpoint(url: string): void {
        const idx = this.motionExternalUrls.findIndex((x) => x == url);
        if (idx !== -1) {
            this.motionExternalUrls.splice(idx, 1);
            this.userSettingsService.set(
                MotionDeviceDiscoveryService.moduleSettingsGroupKey,
                "motionExternalUrls",
                JSON.stringify(this.motionExternalUrls)
            );
        }
    }
}
